import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/core/api/queries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { schoolsApi } from '@/domains/schools/api/schools.api';
import { usersApi } from '@/domains/users/api/users.api';
import { sanitizeAddress } from '@/shared/utils/sanitize-address';
import { CONTACT_ECOTIC } from '@/constants/config';
import { hrefTelefon } from '@/shared/utils/contact-ecotic';
import { esteTelefonNational, EXEMPLU_TELEFON, telefonNational } from '@/shared/utils/telefon-national';
import {
    eroriCampuriCerere,
    MESAJ_ADRESA_INCOMPLETA,
    useRecipienteMutations,
    type EroriCampuriCerere,
} from '../hooks/use-recipiente-mutations';
import { VideoColectareBaterii } from '../components/VideoColectareBaterii';
import {
    adresaLivrareScoala,
    cantitateMinirecipienteValida,
    formateazaFel,
    INFO_CANTITATE_MINIRECIPIENTE,
    PAS_MINIRECIPIENTE,
    stareFel,
    telefonContactImplicit,
    textStare,
    type StareFel,
} from '../utils/stare-recipiente';
import type { FelRecipient } from '../api/waste-container-requests.api';

/**
 * «Comandă recipiente DBA» (SP-04, T4) — ecranul profesorului.
 *
 * 🔴 Eticheta era «Cere recipiente» (decizia lui S1): meniul profesorului are deja
 * trei intrari cu „comandă/comenzi" — «Plasare Comanda», «Raport Comenzi», «Raport
 * Comenzi Respinse» — care toate inseamna **predau deseuri**, iar aici se **cere
 * echipament**. Pe 2026-09-21 ECOTIC a cerut explicit «Comandă recipiente DBA»,
 * cu textele de pe pagina date de ei, verbatim, la „dumneavoastră". Sufixul «DBA»
 * e cel care o deosebeste acum de celelalte trei. URL-ul ramane `/cere-recipiente`
 * (linkuri deja trimise).
 *
 * 🔴 Starea se citeste INAINTE de apasare. Backendul refuza a doua cerere pe
 * acelasi fel cu 409 («Școala a fost deja dotată»), dar profesorul n-are voie sa
 * ajunga acolo: felul deja cerut nu mai are buton. Un ecran care te lasa sa apesi
 * si apoi te refuza te invata ca aplicatia nu stie ce contine.
 *
 * ⚠️ Ce NU are voie sa apara, si e verificat de S1 pe sursa: kilograme sau
 * cantitati de deseu (nu e o predare), termene de livrare, trasee („a plecat un
 * email", „a intrat in ERP"), butoane de anulare, si niciun statut inventat.
 * Textul spune **ce am facut noi**, nu ce se va intampla.
 */

const FELURI: readonly FelRecipient[] = ['RECIPIENT_DBA', 'MINIRECIPIENTE'] as const;

/** Textele ECOTIC (2026-09-21), verbatim. Nu le reformula. */
const DESCRIERE: Record<FelRecipient, string> = {
    RECIPIENT_DBA:
        'Recipientul mare pentru colectarea bateriilor uzate. Recomandăm poziționarea acestuia într-un loc sigur, dar vizibil în școală, pentru promovarea inițiativei în rândul elevilor, profesorilor și al părinților.',
    MINIRECIPIENTE:
        'Cutiile mici pentru colectarea bateriilor uzate. Acestea pot fi oferite, individual, elevilor. Recomandăm ca numărul de minirecipiente menționat în comandă să fie conform numărului de elevi înscriși în program.',
};

/** Scris pe fel, nu derivat cu `toLowerCase()` din nume: „DBA" e acronim. */
const BUTON: Record<FelRecipient, string> = {
    RECIPIENT_DBA: 'Comandă recipient DBA',
    MINIRECIPIENTE: 'Comandă minirecipiente DBA',
};

/**
 * Poza fiecarui fel, in cardul lui. Corectat de ECOTIC pe 2026-10-06: tubul galben cu
 * Ecoterrianul e recipientul DBA (cel mare), iar minirecipientul e cutia de carton. Pana atunci
 * tubul galben statea la minirecipiente, iar la recipient era un tub verde, mai vechi.
 *
 * `width`/`height` sunt dimensiunile intrinseci (raportul), iar cutia din card
 * are latime si inaltime fixe cu `object-contain` — spatiul e rezervat inainte
 * sa soseasca imaginea, deci niciun salt de layout.
 */
const POZA: Record<FelRecipient, { src: string; alt: string; width: number; height: number }> = {
    RECIPIENT_DBA: {
        src: '/recipient-dba.png',
        alt: 'Tub de colectare ECOTICBAT Școala Ecoterrienilor pentru baterii uzate, cu inscripția «Aruncă bateriile uzate aici!»',
        width: 192,
        height: 600,
    },
    MINIRECIPIENTE: {
        src: '/minirecipient-dba.png',
        alt: 'Cutie mică de carton ECOTICBAT pentru baterii uzate, cu inscripția «Bateriile se reciclează! Colectează-le separat!»',
        width: 311,
        height: 480,
    },
};

interface FormularState {
    numeContact: string;
    telefonContact: string;
    cantitate: string;
}

export default function CereRecipientePage() {
    const cereri = api.wasteContainerRequests.own.useQuery({});
    const { data: scoala } = useQuery({
        queryKey: ['schools', 'own'],
        queryFn: () => schoolsApi.getOwn(),
    });
    // Acelasi queryKey ca `useProfileWithForm` — daca profilul a fost deja incarcat
    // (ex. omul a vizitat pagina de Profil), React Query serveste din cache, fara
    // un al doilea request.
    const { data: user } = useQuery({
        queryKey: ['profile'],
        queryFn: () => usersApi.getProfile(),
    });
    const { cere } = useRecipienteMutations();

    /** Ce fel are formularul de confirmare deschis. Cel mult unul odata. */
    const [deschis, setDeschis] = useState<FelRecipient | null>(null);
    /** Ce fel tocmai s-a trimis — rezumatul sta in ACEEASI pozitie ca formularul. */
    const [trimis, setTrimis] = useState<FelRecipient | null>(null);
    const [form, setForm] = useState<FormularState>({
        numeContact: '',
        telefonContact: '',
        cantitate: '',
    });

    /** Erorile pe campuri ale ultimei incercari respinse de server; se golesc la editare. */
    const [eroriServer, setEroriServer] = useState<EroriCampuriCerere>({});
    /** Omul a apasat «Trimite comanda» cu formularul incomplet: de aici se arata ce lipseste. */
    const [incercat, setIncercat] = useState(false);

    const numeScoala = scoala?.numeInstitutieInvatamant ?? 'școala dumneavoastră';

    const deschide = (fel: FelRecipient) => {
        setTrimis(null);
        setDeschis(fel);
        // Precompletare din datele scolii, ca profesorul sa nu retasteze ce am
        // primit deja de la el la inscriere. Raman EDITABILE: persoana de contact
        // pentru livrare poate fi alta decat reprezentantul legal.
        //
        // Telefonul implicit e AL UTILIZATORULUI logat (profilul propriu), nu al
        // scolii — de obicei chiar profesorul e persoana de contact pentru livrare.
        // Fallback pe telefonul scolii doar daca profilul nu are unul completat.
        //
        // 🔴 `telefonNational`: profilul tine telefonul cum a fost scris la inscriere, deci si
        // «+40722123456», iar backendul cere exact 10 cifre. Fara conversie, campul precompletat
        // de NOI era respins cu 400 (07.10) — omul nu tastase nimic gresit.
        setForm({
            numeContact: scoala?.numeReprezentatLegal ?? '',
            telefonContact: telefonNational(telefonContactImplicit(user?.telefon, scoala?.telefon)),
            cantitate: '',
        });
        setEroriServer({});
        setIncercat(false);
    };

    const trimite = (fel: FelRecipient) => {
        const adresa = sanitizeAddress(adresaLivrareScoala(scoala));
        if (!adresa) {
            // Era un `return` mut: butonul nu facea nimic si nu spunea de ce.
            setEroriServer({ adresa: MESAJ_ADRESA_INCOMPLETA });
            return;
        }
        const cantitate = Number(form.cantitate);
        setEroriServer({});
        cere.mutate(
            {
                fel,
                contactPersonName: form.numeContact.trim(),
                contactPersonPhone: telefonNational(form.telefonContact),
                deliveryAddress: adresa,
                ...(fel === 'MINIRECIPIENTE' && Number.isFinite(cantitate) && cantitate > 0
                    ? { cantitate }
                    : {}),
            },
            {
                onSuccess: () => {
                    setDeschis(null);
                    setTrimis(fel);
                },
                // Toastul il da hook-ul; aici erorile ajung SUB campul lor.
                onError: (error) => setEroriServer(eroriCampuriCerere(error)),
            }
        );
    };

    return (
        /* 🔴 Centrarea pe verticala sta pe un COPIL, nu pe elementul cu `overflow-y-auto` --
           tiparul documentat in `ProtocolPage.tsx:53,76` (commit `341ee96c`). `justify-center`
           chiar pe scroller imparte surplusul de o parte si de alta a cutiei, iar partea de SUS
           devine inaccesibila la derulare. Copilul creste liber (`min-height: auto`), deci cand
           un card se desfasoara si depaseste regiunea nu mai exista spatiu liber: centrarea nu
           taie nimic, iar radacina cu `md:overflow-y-auto` deruleaza pana la „Trimite comanda".

           `min-h-dvh` sub `md` (unde `.ecotic-main-content` curge liber), `md:min-h-0` peste:
           fara el un flex item refuza sa scada sub min-content si `overflow-y-auto` n-are ce
           derula. Fara `pt-20 pb-32` (tiparul layout-ului public); loc pentru hamburgerul flotant
           al layout-ului autentificat se face mai jos, cu `pt-14 md:pt-0` pe coloana.

           Scrollerul are latime plina, iar `max-w-2xl` sta pe copil -- tiparul din
           `WasteCollectionFormPage`: altfel scrollerul E coloana ingusta si bara iese in mijlocul
           ecranului. `pr-4` lasa loc intre continut si bara `ecotic-form-scrollbar`. */
        <div className="flex w-full flex-1 min-h-dvh flex-col pr-4 ecotic-form-scrollbar md:min-h-0 md:overflow-y-auto">
            <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-4 p-4">
                <div className="flex flex-1 flex-col justify-center">
                    {/* Pozele stau acum IN card, cate una pe fel (2026-09-21): imaginea arata ce
                        se comanda chiar langa butonul care comanda. Inainte era o singura poza pe
                        toata pagina, `hidden` sub `sm` -- deci invizibila exact pe telefoane.

                        `pt-14 md:pt-0`: sub `md` hamburgerul flotant din `AuthenticatedLayout` acoperea
                        inceputul titlului (masurat la 360px, 2026-09-21). Acelasi tipar ca `AcasaProfesor`. */}
                    <div className="flex min-w-0 flex-col gap-4 pt-14 md:pt-0">
                        <div>
                            <h1 className="font-heading text-fluid-2xl font-semibold text-dark m-0">
                                Comandă recipiente DBA
                            </h1>
                            <p className="mt-2 text-fluid-base text-dark m-0">
                                Puteți solicita recipiente o singură dată, pentru fiecare categorie.
                            </p>
                        </div>

                        {/* Sub intro, INAINTEA cardurilor (decizia owner-ului): disclaimerul se
                            vede inainte de primul buton de comanda. */}
                        <VideoColectareBaterii />

                        {FELURI.map((fel) => {
                            const stare = stareFel(cereri.data, fel);
                            return (
                                <Card
                                    key={fel}
                                    fel={fel}
                                    stare={stare}
                                    seIncarca={cereri.isPending}
                                    deschis={deschis === fel}
                                    trimis={trimis === fel}
                                    numeScoala={numeScoala}
                                    form={form}
                                    setForm={(f) => {
                                        setForm(f);
                                        setEroriServer({});
                                    }}
                                    eroriServer={eroriServer}
                                    incercat={incercat}
                                    onIncercare={() => setIncercat(true)}
                                    seTrimite={cere.isPending}
                                    onDeschide={() => deschide(fel)}
                                    onRenunta={() => setDeschis(null)}
                                    onTrimite={() => trimite(fel)}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

interface CardProps {
    fel: FelRecipient;
    stare: StareFel;
    seIncarca: boolean;
    deschis: boolean;
    trimis: boolean;
    numeScoala: string;
    form: FormularState;
    setForm: (f: FormularState) => void;
    eroriServer: EroriCampuriCerere;
    incercat: boolean;
    onIncercare: () => void;
    seTrimite: boolean;
    onDeschide: () => void;
    onRenunta: () => void;
    onTrimite: () => void;
}

function Card({
    fel,
    stare,
    seIncarca,
    deschis,
    trimis,
    numeScoala,
    form,
    setForm,
    eroriServer,
    incercat,
    onIncercare,
    seTrimite,
    onDeschide,
    onRenunta,
    onTrimite,
}: CardProps) {
    const nume = formateazaFel(fel);
    const rand = textStare(stare, fel);
    const cereCantitate = fel === 'MINIRECIPIENTE';
    const telefon = telefonNational(form.telefonContact);
    const numeCompletat = form.numeContact.trim().length > 0;
    const telefonValid = esteTelefonNational(telefon);
    const cantitateValida = !cereCantitate || cantitateMinirecipienteValida(form.cantitate);
    const cantitateCompletata = form.cantitate.trim().length > 0;
    const completat = numeCompletat && telefonValid && cantitateValida;

    // Eroarea serverului are intaietate; cea locala apare abia dupa o incercare de trimitere,
    // ca sa nu certe omul in timp ce tasteaza.
    const eroareNume =
        eroriServer.numeContact ??
        (incercat && !numeCompletat ? 'Completați persoana de contact.' : undefined);
    const eroareTelefon =
        eroriServer.telefonContact ??
        (incercat && !telefonValid
            ? telefon.length === 0
                ? `Completați telefonul persoanei de contact. Exemplu: ${EXEMPLU_TELEFON}.`
                : `Telefonul trebuie să aibă exact 10 cifre. Exemplu: ${EXEMPLU_TELEFON}.`
            : undefined);
    // Valoarea gresita apare cu rosu IMEDIAT, cat omul tasteaza (cerinta ECOTIC, 2026-10-16): regula
    // „multiplu de 100" nu e evidenta, iar textul de informare de deasupra o spune deja. Campul gol
    // ramane pe regula de mai sus — eroare abia dupa o incercare de trimitere.
    const eroareCantitate =
        eroriServer.cantitate ??
        (cereCantitate && cantitateCompletata && !cantitateValida
            ? 'Cantitatea trebuie să fie 100 sau un multiplu de 100 (200, 300, 400…).'
            : incercat && !cantitateValida
              ? 'Completați câte minirecipiente comandați.'
              : undefined);

    return (
        <section
            data-fel={fel}
            data-stare={stare.stare}
            className="rounded-lg border border-hairline bg-white p-4"
        >
            <div className="flex items-start gap-3">
                {/* Cutie FIXA (w x h), `object-contain` inauntru: cele doua poze au rapoarte
                    diferite (0,32 tubul, 0,65 cutia), iar spatiul e rezervat inainte de incarcare. */}
                <img
                    src={POZA[fel].src}
                    alt={POZA[fel].alt}
                    width={POZA[fel].width}
                    height={POZA[fel].height}
                    className="h-40 w-24 shrink-0 object-contain sm:h-56 sm:w-32"
                />
                <div className="min-w-0 flex-1">
                    <h2 className="m-0 font-semibold text-dark">{nume}</h2>
                    <p className="m-0 mt-1 text-small text-gray-600">{DESCRIERE[fel]}</p>
                </div>
            </div>

            {/* Cat timp nu stim, nu afirmam nici „disponibil", nici „cerut": un buton
                desenat optimist ar fi o promisiune neverificata, iar un rand de stare
                ar fi o afirmatie despre server pe care n-am primit-o. */}
            {seIncarca && (
                <p className="mt-3 text-small text-gray-600 m-0">Se verifică ce ați comandat deja…</p>
            )}

            {/* Randul de stare: felul deja cerut nu mai are buton. Textul vine din
                modulul pur, ca sa fie acoperit de teste — cuvintele sunt parte din
                contract, nu decor. */}
            {!seIncarca && rand && (
                <div className="mt-3 rounded-lg bg-light p-3 text-small text-dark">
                    <p className="m-0">{rand}</p>
                    {stare.stare === 'preluata' && stare.dataCererii && (
                        /* Intrebarea „cand am cerut?" nu dispare fiindca s-a mai
                           intamplat ceva; raspunsul ramane, ca linie secundara. */
                        <p className="m-0 mt-1 text-smaller text-gray-600">
                            Ați comandat pe {stare.dataCererii}.
                        </p>
                    )}
                </div>
            )}

            {/* Rezumatul a ce s-a trimis, in ACEEASI pozitie ca formularul (regula
                casetelor: chenar plin inainte de actiune, umplutura dupa).
                SE-18 (owner, 2026-09-16): text primit exact de la S0, cu telefonul din
                CONTACT_ECOTIC. Spune ce se intampla mai departe (ECOTIC confirma si
                anunta), nu doar ce am facut noi -- diferit de vechea regula "nu se
                promite nimic", pentru ca acum exista un flux real de confirmare/livrare
                de urmarit, nu doar o cerere trimisa in gol. */}
            {trimis && (
                <div
                    data-recipiente-rezumat
                    className="mt-3 rounded-lg bg-light p-3 text-small text-dark"
                >
                    <p className="m-0">
                        Comanda a fost trimisă; ECOTIC o confirmă și vă anunță; pentru întrebări:{' '}
                        {hrefTelefon(CONTACT_ECOTIC.telefon) ? (
                            <a href={hrefTelefon(CONTACT_ECOTIC.telefon)} className="underline">
                                {CONTACT_ECOTIC.telefon}
                            </a>
                        ) : (
                            CONTACT_ECOTIC.telefon
                        )}
                        .
                    </p>
                </div>
            )}

            {!seIncarca && stare.stare === 'disponibil' && !deschis && !trimis && (
                <Button type="button" onClick={onDeschide} className="mt-3 min-h-11 w-full">
                    {BUTON[fel]}
                </Button>
            )}

            {/* Caseta de confirmare, INAINTE de actiune: cererea e una pe editie si
                nu se poate anula din aplicatie, deci se spune inainte, nu dupa. */}
            {deschis && (
                <div
                    data-recipiente-confirmare
                    className="mt-3 rounded-lg border border-magenta p-3"
                >
                    <p className="m-0 text-small text-dark">
                        Comandați <strong>{nume}</strong> pentru {numeScoala}. Se poate comanda o
                        singură dată pe ediție.
                    </p>

                    {cereCantitate && (
                        <div className="mt-3">
                            <Label htmlFor="cantitate-minirecipiente">Câte minirecipiente</Label>
                            {/* Textul ECOTIC, permanent: regula se citeste INAINTE de tastare. */}
                            <p
                                id="cantitate-minirecipiente-info"
                                className="m-0 mt-1 text-small text-gray-600"
                            >
                                {INFO_CANTITATE_MINIRECIPIENTE}
                            </p>
                            <Input
                                id="cantitate-minirecipiente"
                                type="number"
                                min={PAS_MINIRECIPIENTE}
                                step={PAS_MINIRECIPIENTE}
                                placeholder={String(PAS_MINIRECIPIENTE)}
                                inputMode="numeric"
                                className="mt-1 min-h-11"
                                value={form.cantitate}
                                onChange={(e) => setForm({ ...form, cantitate: e.target.value })}
                                aria-invalid={eroareCantitate ? true : undefined}
                                aria-describedby={
                                    eroareCantitate
                                        ? 'cantitate-minirecipiente-info cantitate-minirecipiente-eroare'
                                        : 'cantitate-minirecipiente-info'
                                }
                            />
                            {eroareCantitate && (
                                <p
                                    id="cantitate-minirecipiente-eroare"
                                    role="alert"
                                    className="m-0 mt-1 text-small text-error"
                                >
                                    {eroareCantitate}
                                </p>
                            )}
                        </div>
                    )}

                    <div className="mt-3">
                        <Label htmlFor="nume-contact">Persoana de contact pentru livrare</Label>
                        <Input
                            id="nume-contact"
                            className="mt-1 min-h-11"
                            value={form.numeContact}
                            onChange={(e) => setForm({ ...form, numeContact: e.target.value })}
                            aria-invalid={eroareNume ? true : undefined}
                            aria-describedby={eroareNume ? 'nume-contact-eroare' : undefined}
                        />
                        {eroareNume && (
                            <p id="nume-contact-eroare" role="alert" className="m-0 mt-1 text-small text-error">
                                {eroareNume}
                            </p>
                        )}
                    </div>

                    <div className="mt-3">
                        <Label htmlFor="telefon-contact">Telefonul persoanei de contact</Label>
                        <Input
                            id="telefon-contact"
                            type="tel"
                            inputMode="tel"
                            className="mt-1 min-h-11"
                            autoComplete="tel-national"
                            value={form.telefonContact}
                            onChange={(e) => setForm({ ...form, telefonContact: e.target.value })}
                            aria-invalid={eroareTelefon ? true : undefined}
                            aria-describedby={eroareTelefon ? 'telefon-contact-eroare' : undefined}
                        />
                        {eroareTelefon && (
                            <p
                                id="telefon-contact-eroare"
                                role="alert"
                                className="m-0 mt-1 text-small text-error"
                            >
                                {eroareTelefon}
                            </p>
                        )}
                    </div>

                    <p className="mt-3 text-smaller text-gray-600 m-0">
                        Livrarea merge la adresa școlii din contul dumneavoastră. Dacă s-a schimbat,
                        actualizați-o din datele școlii înainte să comandați.
                    </p>
                    {eroriServer.adresa && (
                        <p
                            data-recipiente-eroare-adresa
                            role="alert"
                            className="m-0 mt-1 text-small text-error"
                        >
                            {eroriServer.adresa}
                        </p>
                    )}

                    {/* Un singur buton PLIN in grup: actiunea principala. „Renunță" e
                        conturat, ca sa nu concureze cu ea.

                        `aria-disabled`, nu `disabled`: `disabled:opacity-50` din
                        `buttonVariants` sterge eticheta la 50 %, adica sub pragul de
                        contrast, exact cand omul are nevoie sa citeasca de ce nu merge.
                        Blocarea e in handler, nu in atribut — un `preventDefault` n-ar
                        opri `onClick`, deci ar fi o protectie doar aparenta. */}
                    <Button
                        type="button"
                        aria-disabled={!completat || seTrimite}
                        onClick={() => {
                            if (seTrimite) return;
                            if (!completat) {
                                // Butonul „stins" raspunde totusi: arata sub fiecare camp ce lipseste.
                                onIncercare();
                                return;
                            }
                            onTrimite();
                        }}
                        className="mt-3 min-h-11 w-full"
                    >
                        {seTrimite ? 'Se trimite…' : 'Trimite comanda'}
                    </Button>
                    <Button
                        type="button"
                        onClick={onRenunta}
                        className="mt-2 min-h-11 w-full border border-input bg-white font-semibold text-dark hover:bg-light"
                    >
                        Renunță
                    </Button>
                </div>
            )}
        </section>
    );
}
